import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';

import {
  createFacebookAuthURL,
  isFacebookSDKBlocked,
  isFacebookSDKLoaded,
  loadFacebookSDK,
} from '../../services/Facebook';
import type { ReduxState } from '../../store';
import Loader from '../Loader';

import styles from './socialLogin.module.scss';

interface Props {
  appId: string;
  version: string;
  scope?: string;
  children: React.ReactNode;
  onSuccess(response: FacebookSDK.AuthResponse): void;
  onFailure?(error: Error, blocked?: boolean): void;
}

export default function FacebookLoginButton(props: Props) {
  const language = useSelector((store: ReduxState) => store.app.language);
  const [loading, setLoading] = useState(!isFacebookSDKLoaded());
  const location = useLocation();
  const scope = props.scope ?? 'public_profile,email';

  const handleClick = useCallback(() => {
    if (loading) return;
    if (isFacebookSDKBlocked()) {
      navigate(createFacebookAuthURL(location, scope));
      return;
    }
    setLoading(true);
    try {
      window.FB.getLoginStatus(status => {
        if (status.status === 'connected') {
          props.onSuccess(status.authResponse);
        } else {
          window.FB.login(
            response => {
              if (!response.accessToken) {
                props.onFailure?.(response.error);
                setLoading(false);
              } else {
                props.onSuccess(response);
              }
            },
            {
              appId: props.appId,
              version: props.version,
              scope,
            },
          );
        }
      });
    } catch (error) {
      props.onFailure?.(error);
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    return loadFacebookSDK(language, () => {
      setLoading(false);
    });
  }, []);

  return (
    <button className={styles.facebook} onClick={handleClick} type="button">
      {loading ? <Loader dotClassName="bg-white" /> : props.children}
    </button>
  );
}
